import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import BaseButton from '../../components/BasicComponent/BaseButton'
import BaseInput from '../../components/BasicComponent/BaseInput'
import BasePopup from '../../components/BasicComponent/BasePopup'
import SafeImage from '../../components/BasicComponent/SafeImage'
import { VEHICLE_TYPE } from '../../constants/global'
import { PATH } from '../../constants/router'
import { OPTIONS_VEHICLE_TYPE_INFO_VEHICLE } from '../../constants/vehicle'
import { normalizePlate, sanitizePlateInput, validatePlateNumber } from '../../helper/validatorPlateNumber'
import FixedBottom from '../../components/BasicComponent/FixedBottom'
import DraggableScroll from '../../components/BasicComponent/DraggableScroll'
import LayoutPartner from '../../components/FeaturesComponent/layoutPartner'
import AutomatedTrafficFineNotificationStep from '../../components/FeaturesComponent/automatedTrafficFineNotificationStep'
import { useTrafficFineVehicleContext } from '../../context/TrafficFineVehicleContext'
import { useAppUserContext } from '../../context/AppUserContext'
import './index.scss'

function RemoveIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.4" />
      <path d="M7 7 13 13M13 7l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function getVehicleTypeInfo(vehicleType) {
  return OPTIONS_VEHICLE_TYPE_INFO_VEHICLE.find((item) => Number(item.vehicleType) === Number(vehicleType)) || OPTIONS_VEHICLE_TYPE_INFO_VEHICLE[0]
}

function getVehicleListTitle(packageVehicleType) {
  const normalizedPackageVehicleType = Number(packageVehicleType)

  if (normalizedPackageVehicleType === VEHICLE_TYPE.CAR) {
    return 'Danh sách ô tô'
  }

  if ([VEHICLE_TYPE.MOTOBIKE, VEHICLE_TYPE.ELECTRIC_MOTORBIKE].includes(normalizedPackageVehicleType)) {
    return 'Danh sách xe máy'
  }

  return 'Danh sách xe'
}

function isRegistrationVehicleTypeCompatible(packageVehicleType, registrationVehicleType) {
  const normalizedPackageVehicleType = Number(packageVehicleType)
  const normalizedRegistrationVehicleType = Number(registrationVehicleType)

  if (!normalizedPackageVehicleType || !normalizedRegistrationVehicleType) {
    return false
  }

  if (normalizedPackageVehicleType === normalizedRegistrationVehicleType) {
    return true
  }

  if (
    normalizedPackageVehicleType === VEHICLE_TYPE.MOTOBIKE &&
    normalizedRegistrationVehicleType === VEHICLE_TYPE.ELECTRIC_MOTORBIKE
  ) {
    return true
  }

  return false
}

const VIRTUAL_KEYBOARD_HEIGHT_THRESHOLD = 120

function getViewportHeight() {
  if (typeof window === 'undefined') {
    return 0
  }

  return window.visualViewport?.height || window.innerHeight || 0
}

export default function AutoTrafficFineNotificationVehicles() {
  const history = useHistory()
  const location = useLocation()
  const locationState = location.state || {}
  const { userData } = useAppUserContext()

  const phoneToUse = userData?.phoneNumberFromSdk || locationState?.phoneNumberFromSdk || ''
  const phoneWithPrefixToUse = userData?.phoneNumberWithUsernamePrefix || locationState?.phoneNumberWithUsernamePrefix || ''

  const { listVehicleNotRegistered, listVehicleRegisteredPackage } = useTrafficFineVehicleContext()

  const passedServices = locationState.services || []
  const trafficFineRegistrationProduct = passedServices[0] || null

  let packageVehicleType = trafficFineRegistrationProduct?.vehicleType ? Number(trafficFineRegistrationProduct.vehicleType) : undefined
  if (trafficFineRegistrationProduct && !packageVehicleType) {
    packageVehicleType = VEHICLE_TYPE.CAR
  }
  const registrationVehicleType = locationState?.vehicleType ? Number(locationState.vehicleType) : undefined

  const isCompatibleRegistrationVehicleType = isRegistrationVehicleTypeCompatible(packageVehicleType, registrationVehicleType)

  const activeVehicleType = isCompatibleRegistrationVehicleType ? registrationVehicleType : packageVehicleType || registrationVehicleType
  const activeVehicleTypeInfo = activeVehicleType ? getVehicleTypeInfo(activeVehicleType) : null
  const maxVehicles = Math.max(Number(trafficFineRegistrationProduct?.productSize) || 1, 1)
  const vehicleListTitle = getVehicleListTitle(packageVehicleType)

  const [plateInput, setPlateInput] = useState('')
  const [plateError, setPlateError] = useState('')
  const [selectedVehicles, setSelectedVehicles] = useState([])
  const [popupConfig, setPopupConfig] = useState(null)
  const [isPlateInputFocused, setIsPlateInputFocused] = useState(false)
  const [isVirtualKeyboardOpen, setIsVirtualKeyboardOpen] = useState(false)
  const blurTimeoutRef = useRef(null)
  const viewportHeightBeforeKeyboardRef = useRef(0)

  const selectedCount = selectedVehicles.length
  const canContinue = selectedCount > 0

  const isContinueDisabled = !canContinue
  const shouldHideContinueButton = isVirtualKeyboardOpen

  const registeredVehicleKeys = useMemo(() => {
    return new Set(
      listVehicleRegisteredPackage
        .map((item) => {
          const plate = normalizePlate(item?.vehicleIdentity || item?.vehicleIdentityNumber || item?.customerRecordPlatenumber || item?.plate || '')
          const vehicleType = Number(item?.vehicleType)

          if (!plate || !vehicleType) {
            return null
          }

          return `${plate}-${vehicleType}`
        })
        .filter(Boolean)
    )
  }, [listVehicleRegisteredPackage])

  const suggestedVehicles = useMemo(() => {
    if (!activeVehicleType) {
      return []
    }

    const uniqueVehicles = new Map()

    listVehicleNotRegistered.forEach((item) => {
      const plate = normalizePlate(item?.vehicleIdentity || item?.vehicleIdentityNumber || item?.customerRecordPlatenumber || item?.plate || '')
      const vehicleType = Number(item?.vehicleType)

      if (!plate || vehicleType !== activeVehicleType) {
        return
      }

      const key = `${plate}-${vehicleType}`

      if (!uniqueVehicles.has(key)) {
        uniqueVehicles.set(key, {
          plate,
          vehicleType
        })
      }
    })

    return [...uniqueVehicles.values()]
  }, [activeVehicleType, listVehicleNotRegistered])

  const closePopup = () => {
    setPopupConfig(null)
  }

  const openAlertPopup = (content, title = 'Thông báo') => {
    setPopupConfig({
      title,
      content,
      confirmText: 'Đã hiểu'
    })
  }

  const openConfirmPopup = ({ title, content, onConfirm, closeText = 'Hủy', confirmText = 'Tiếp tục' }) => {
    setPopupConfig({
      title,
      content,
      onConfirm,
      closeText,
      confirmText
    })
  }

  useEffect(() => {
    if (!trafficFineRegistrationProduct) {
      history.replace(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_SERVICES)
    }
  }, [history, trafficFineRegistrationProduct])

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isPlateInputFocused) {
      viewportHeightBeforeKeyboardRef.current = 0
      setIsVirtualKeyboardOpen(false)
      return undefined
    }

    const updateVirtualKeyboardState = () => {
      const currentViewportHeight = getViewportHeight()

      if (!currentViewportHeight) {
        setIsVirtualKeyboardOpen(false)
        return
      }

      viewportHeightBeforeKeyboardRef.current = Math.max(
        viewportHeightBeforeKeyboardRef.current,
        currentViewportHeight
      )

      const keyboardHeight = viewportHeightBeforeKeyboardRef.current - currentViewportHeight
      setIsVirtualKeyboardOpen(keyboardHeight > VIRTUAL_KEYBOARD_HEIGHT_THRESHOLD)
    }

    const visualViewport = window.visualViewport

    updateVirtualKeyboardState()

    visualViewport?.addEventListener('resize', updateVirtualKeyboardState)
    visualViewport?.addEventListener('scroll', updateVirtualKeyboardState)
    window.addEventListener('resize', updateVirtualKeyboardState)

    return () => {
      visualViewport?.removeEventListener('resize', updateVirtualKeyboardState)
      visualViewport?.removeEventListener('scroll', updateVirtualKeyboardState)
      window.removeEventListener('resize', updateVirtualKeyboardState)
    }
  }, [isPlateInputFocused])

  const addVehicle = (plate) => {
    const safePlate = normalizePlate(plate)

    if (!safePlate) {
      setPlateError('Vui lòng nhập biển số xe')
      return false
    }

    if (!activeVehicleType) {
      setPlateError('')
      openAlertPopup('Không xác định được loại xe. Vui lòng quay lại chọn gói dịch vụ.')
      return false
    }

    const registeredVehicleKey = `${safePlate}-${activeVehicleType}`
    if (registeredVehicleKeys.has(registeredVehicleKey)) {
      setPlateError('')
      openAlertPopup('Biển số này đã nằm trong danh sách xe đã mua gói. Vui lòng chọn xe khác.')
      return false
    }

    if (selectedVehicles.some((vehicle) => vehicle.plate === safePlate && Number(vehicle.vehicleType) === Number(activeVehicleType))) {
      setPlateError('Biển số này đã có trong danh sách xe đăng ký gói.')
      return false
    }

    if (selectedVehicles.length >= maxVehicles) {
      setPlateError('')
      openAlertPopup('Thao tác không thành công do đã đạt giới hạn số lượng xe quy định.')
      return false
    }

    setSelectedVehicles((prevVehicles) => [
      ...prevVehicles,
      {
        plate: safePlate,
        vehicleType: activeVehicleType,
        id: safePlate
      }
    ])
    setPlateError('')
    return true
  }

  const handleAddVehicleFromInput = () => {
    const safePlate = normalizePlate(plateInput)
    const validationError = validatePlateNumber(safePlate)

    if (validationError) {
      setPlateError(validationError)
      return
    }

    const isAdded = addVehicle(safePlate)

    if (isAdded) {
      setPlateInput('')
    }
  }

  const handleRemoveVehicle = (plate, vehicleType) => {
    setSelectedVehicles((prevVehicles) =>
      prevVehicles.filter((vehicle) => !(vehicle.plate === plate && Number(vehicle.vehicleType) === Number(vehicleType)))
    )
    setPlateError('')
  }

  const handleContinue = () => {
    history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_PAYMENT, {
      vehicles: selectedVehicles,
      services: passedServices,
      email: locationState?.email || '',
      phoneNumberFromSdk: phoneToUse,
      phoneNumberWithUsernamePrefix: phoneWithPrefixToUse
    })
  }

  if (!trafficFineRegistrationProduct) {
    return null
  }

  return (
    <LayoutPartner headerTitle={'Tự động thông báo phạt nguội'} blendHeaderWithBody={false}>
      <div className="AutoTrafficFineNotificationVehicles">
        <AutomatedTrafficFineNotificationStep step={1} />
        <div className="AutoTrafficFineNotificationVehicles_purpose text-center">
          Chọn/ nhập {maxVehicles} phương tiện để tiếp tục
        </div>

        <div className="AutoTrafficFineNotificationVehicles_infoCard">
          <div className="AutoTrafficFineNotificationVehicles_infoCardTitle">Thông tin xe</div>
          <div className="AutoTrafficFineNotificationVehicles_inputRow">
            <BaseInput
              value={plateInput}
              onChange={(event) => {
                const value = sanitizePlateInput(event.target.value)
                setPlateInput(value)
                setPlateError('')
              }}
              showClear={Boolean(plateInput)}
              onClear={() => {
                setPlateInput('')
                setPlateError('')
              }}
              onFocus={() => {
                if (blurTimeoutRef.current) {
                  window.clearTimeout(blurTimeoutRef.current)
                  blurTimeoutRef.current = null
                }

                viewportHeightBeforeKeyboardRef.current = getViewportHeight()
                setIsPlateInputFocused(true)
              }}
              onBlur={() => {
                blurTimeoutRef.current = window.setTimeout(() => {
                  setIsPlateInputFocused(false)
                  setIsVirtualKeyboardOpen(false)
                  viewportHeightBeforeKeyboardRef.current = 0
                  blurTimeoutRef.current = null
                }, 180)
              }}
              placeholder="Ví dụ: 29A12345"
              error={plateError}
              buttonRight={
                <BaseButton
                  type="right"
                  className="AutoTrafficFineNotificationVehicles_addButton"
                  disabled={!plateInput}
                  onClick={handleAddVehicleFromInput}>
                  Thêm
                </BaseButton>
              }
            />
          </div>
        </div>

        {suggestedVehicles.length > 0 ? (
          <div className="AutoTrafficFineNotificationVehicles_suggestions">
            <DraggableScroll>
              <div className="AutoTrafficFineNotificationVehicles_suggestionsTrack">
                {suggestedVehicles.map((vehicle) => {
                  const isSelected = selectedVehicles.some((selectedVehicle) => selectedVehicle.plate === vehicle.plate)

                  return (
                    <button
                      key={`${vehicle.plate}-${vehicle.vehicleType}`}
                      type="button"
                      className={`AutoTrafficFineNotificationVehicles_suggestionItem ${
                        isSelected ? 'AutoTrafficFineNotificationVehicles_suggestionItem_active' : ''
                      }`.trim()}
                      onClick={() => {
                        if (!isSelected) {
                          addVehicle(vehicle.plate)
                        }
                      }}>
                      <SafeImage src={activeVehicleTypeInfo?.iconUrl} className="AutoTrafficFineNotificationVehicles_suggestionIcon" />
                      <span>{vehicle.plate}</span>
                    </button>
                  )
                })}
              </div>
            </DraggableScroll>
          </div>
        ) : null}

        <div className="AutoTrafficFineNotificationVehicles_listHeader">{vehicleListTitle} ({selectedCount}/{maxVehicles})</div>

        <div className="AutoTrafficFineNotificationVehicles_list">
          {selectedVehicles.map((vehicle) => {
            const vehicleTypeInfo = getVehicleTypeInfo(vehicle.vehicleType)

            return (
              <div key={`${vehicle.plate}-${vehicle.vehicleType}`} className="AutoTrafficFineNotificationVehicles_vehicleItem">
                <div className="AutoTrafficFineNotificationVehicles_vehicleInfo">
                  <SafeImage src={vehicleTypeInfo?.iconUrl} className="AutoTrafficFineNotificationVehicles_vehicleIcon" />

                  <div className="AutoTrafficFineNotificationVehicles_vehicleText">
                    <div className="AutoTrafficFineNotificationVehicles_vehiclePlate">{vehicle.plate}</div>
                    <div className="AutoTrafficFineNotificationVehicles_vehicleType">{vehicleTypeInfo?.label}</div>
                  </div>
                </div>

                <button
                  type="button"
                  className="AutoTrafficFineNotificationVehicles_removeButton"
                  onClick={() => handleRemoveVehicle(vehicle.plate, vehicle.vehicleType)}
                  aria-label={`Xóa xe ${vehicle.plate}`}>
                  <RemoveIcon />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {!shouldHideContinueButton ? (
        <FixedBottom elementPaddingBottom="LayoutPartner">
          <BaseButton
            disabled={isContinueDisabled}
            onClick={() => {
              if (selectedCount < maxVehicles) {
                openConfirmPopup({
                  title: `Bạn chưa chọn đủ ${maxVehicles} xe`,
                  content: 'Sau khi thanh toán, bạn sẽ không thể thêm hoặc thay đổi xe. Bạn có muốn tiếp tục không?',
                  onConfirm: handleContinue
                })
                return
              }

              handleContinue()
            }}>
            Tiếp theo
          </BaseButton>
        </FixedBottom>
      ) : null}

      <BasePopup
        open={!!popupConfig}
        onClose={closePopup}
        onConfirm={() => {
          const nextAction = popupConfig?.onConfirm
          closePopup()
          nextAction?.()
        }}
        title={popupConfig?.title || 'Thông báo'}
        content={popupConfig?.content || ''}
        closeText={popupConfig?.closeText || ''}
        confirmText={popupConfig?.confirmText || ''}
      />

    </LayoutPartner>
  )
}
